import PropTypes from "prop-types";
import React, { useState } from "react";
import { Row, Col, Alert, Card, CardBody, Container, FormFeedback, Input, Label, Form } from "reactstrap";

import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

// real backend client (HttpOnly-cookie aware axios instance)
import api from "../../helpers/api";

// import images
import profile from "../../assets/images/auth-bg-1.jpg";
import logo from "../../assets/images/logo-sm.png";

// Mirrors the backend ResetPasswordTokenDTO @Pattern: at least one lowercase,
// one uppercase, one digit and one special character.
const PASSWORD_COMPLEXITY = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()\-_=+\[\]{}|;:'",.<>\/`~]).+$/;

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  document.title = t('shared.reset_password', "Reset Password") + " | Arcmedix";

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .min(8, t('shared.password_min_8', "Password must be at least 8 characters"))
        .matches(PASSWORD_COMPLEXITY, t('shared.password_complexity',
          "Password must include an uppercase letter, a lowercase letter, a number, and a special character"))
        .required(t('shared.please_enter_password', "Please Enter Your Password")),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword')], t('shared.passwords_do_not_match', "Passwords do not match"))
        .required(t('shared.please_enter_password', "Please Enter Your Password")),
    }),
    onSubmit: async (values) => {
      if (!token) {
        setErrorMsg(t('shared.reset_link_missing',
          "This reset link is missing its token. Please use the link from your email."));
        return;
      }
      setSubmitting(true);
      setErrorMsg("");
      setSuccessMsg("");
      try {
        await api.post('auth/reset-password', { token, newPassword: values.newPassword });
        setSuccessMsg(t('shared.password_reset_success',
          "Your password has been reset. You can now sign in with your new password."));
        setTimeout(() => navigate('/login'), 2500);
      } catch (err) {
        if (err.response?.status === 429) {
          setErrorMsg(t('shared.too_many_requests', "Too many requests. Please try again later."));
        } else {
          setErrorMsg(t('shared.invalid_or_expired_reset_link',
            "This password reset link is invalid or has expired. Please request a new one."));
        }
      } finally {
        setSubmitting(false);
      }
    }
  });

  return (
    <React.Fragment>

      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-primary bg-primary-subtle">
                  <Row>
                    <Col xs={7}>
                      <div className="text-primary p-4">
                        <h5 className="text-primary">{t('shared.reset_password', "Reset Password")}</h5>
                        <p>{t('shared.sign_in_to_continue', "Sign in to continue to Arcmedix")}</p>
                      </div>
                    </Col>
                    <Col className="col-5 align-self-end">
                      <img src={profile} alt="" className="img-fluid" />
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div>
                    <Link to="/">
                      <div className="avatar-md profile-user-wid mb-4">
                        <span className="avatar-title rounded-circle bg-light">
                          <img
                            src={logo}
                            alt=""
                            className="rounded-circle"
                            height="34"
                          />
                        </span>
                      </div>
                    </Link>
                  </div>
                  <div className="p-2">
                    {errorMsg ? (
                      <Alert color="danger" style={{ marginTop: "13px" }}>
                        {errorMsg}
                      </Alert>
                    ) : null}
                    {successMsg ? (
                      <Alert color="success" style={{ marginTop: "13px" }}>
                        {successMsg}
                      </Alert>
                    ) : null}

                    {!token ? (
                      <div className="mt-3 text-center">
                        <Alert color="danger">
                          {t('shared.reset_link_missing',
                            "This reset link is missing its token. Please use the link from your email.")}
                        </Alert>
                        <Link to="/forgot-password" className="btn btn-primary w-md">
                          {t('shared.forget_password', "Forget Password")}
                        </Link>
                      </div>
                    ) : (
                      <Form
                        className="form-horizontal"
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                      >
                        <div className="mb-3">
                          <Label className="form-label">{t('shared.new_password', "New Password")}</Label>
                          <Input
                            name="newPassword"
                            className="form-control"
                            placeholder={t('shared.enter_new_password', "Enter new password")}
                            type="password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.newPassword || ""}
                            invalid={
                              validation.touched.newPassword && validation.errors.newPassword ? true : false
                            }
                          />
                          {validation.touched.newPassword && validation.errors.newPassword ? (
                            <FormFeedback type="invalid"><div>{validation.errors.newPassword}</div></FormFeedback>
                          ) : null}
                        </div>
                        <div className="mb-3">
                          <Label className="form-label">{t('shared.confirm_new_password', "Confirm New Password")}</Label>
                          <Input
                            name="confirmPassword"
                            className="form-control"
                            placeholder={t('shared.confirm_new_password', "Confirm New Password")}
                            type="password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.confirmPassword || ""}
                            invalid={
                              validation.touched.confirmPassword && validation.errors.confirmPassword ? true : false
                            }
                          />
                          {validation.touched.confirmPassword && validation.errors.confirmPassword ? (
                            <FormFeedback type="invalid"><div>{validation.errors.confirmPassword}</div></FormFeedback>
                          ) : null}
                        </div>
                        <Row className="mb-3">
                          <Col className="text-end">
                            <button
                              className="btn btn-primary w-md "
                              type="submit"
                              disabled={submitting || !!successMsg}
                            >
                              {submitting ? t('shared.please_wait', "Please wait...") : t('shared.reset_password', "Reset Password")}
                            </button>
                          </Col>
                        </Row>
                      </Form>
                    )}
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center">
                <p>
                  {t('shared.go_back_to', "Go back to")}{" "}
                  <Link to="/login" className="font-weight-medium text-primary">
                    {t('shared.login', "Login")}
                  </Link>{" "}
                </p>
                <p className="text-white-50">
                  {t('shared.copyright', 'Copyright ©')} {new Date().getFullYear()} {t('shared.arcmedix_all_rights_reserved', 'Arcmedix. All rights reserved')}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

ResetPasswordPage.propTypes = {
  t: PropTypes.func,
};

export default ResetPasswordPage;
